import React, { useState, useRef } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Upload, CheckCircle2, AlertTriangle, FileArchive, Package, X, ChevronRight, Image as ImageIcon, Loader2 } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import AdminLayout from '@/components/admin/AdminLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminImages = () => {
  const { user, loading } = useAuth();
  const fileInputRef = useRef(null);

  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);

  // Loading state
  if (loading || user === null) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#2A5C43]" />
      </div>
    );
  }

  // Admin protection
  if (!user || user.role !== 'admin') {
    return <Navigate to="/login?redirect=/admin/imagenes" replace />;
  }

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.zip')) {
      setError('Debes seleccionar un archivo ZIP');
      return;
    }
    if (file.size > 200 * 1024 * 1024) {
      setError('El archivo es demasiado grande (máximo 200 MB)');
      return;
    }
    setError('');
    setResult(null);
    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    setError('');
    setResult(null);
    setProgress(0);

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const { data } = await axios.post(`${API}/admin/products/bulk-images`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (evt) => {
          if (evt.total) {
            setProgress(Math.round((evt.loaded * 100) / evt.total));
          }
        },
        timeout: 300000 // 5 minutes
      });
      setResult(data);
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      setError(typeof msg === 'string' ? msg : 'Error al subir el archivo');
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const clearSelection = () => {
    setSelectedFile(null);
    setError('');
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <AdminLayout>
    <div className="max-w-5xl mx-auto">
      {/* Breadcrumbs */}
      <nav className="flex items-center gap-1 text-xs text-[#57534E] mb-6">
        <Link to="/admin" className="hover:text-[#2A5C43]">Dashboard</Link>
        <ChevronRight className="w-3 h-3" />
        <span className="text-[#1C1917] font-medium">Carga masiva de imágenes</span>
      </nav>

      <div className="mb-10">
        <p className="text-xs tracking-[0.25em] uppercase text-[#D47A4A] font-semibold mb-2">Panel administrativo</p>
        <h1 className="font-display font-bold text-4xl sm:text-5xl text-[#1C1917] tracking-tight mb-3">
          Carga masiva de imágenes
        </h1>
        <p className="text-[#57534E]">
          Sube un archivo ZIP con las imágenes de tus productos. El sistema las asocia automáticamente a cada producto según el nombre del archivo.
        </p>
      </div>

      {/* Instructions */}
      <div className="bg-[#F7F2E8]/50 rounded-2xl border border-[#E7E5E4] p-6 mb-8">
        <h3 className="font-display font-semibold text-[#1C1917] mb-3 flex items-center gap-2">
          <FileArchive className="w-5 h-5 text-[#2A5C43]" />
          Convención de nombres
        </h3>
        <p className="text-sm text-[#57534E] mb-3">
          Cada imagen debe nombrarse con una de estas opciones (mayúsculas/minúsculas indiferente):
        </p>
        <ul className="space-y-2 text-sm text-[#1C1917]">
          <li className="flex items-start gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-[#2A5C43] mt-2 shrink-0" />
            <span><strong>Referencia del producto:</strong> <code className="bg-white px-2 py-0.5 rounded text-xs">2103.jpg</code> — coincide con el SKU de la variante</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-[#2A5C43] mt-2 shrink-0" />
            <span><strong>Código de barras:</strong> <code className="bg-white px-2 py-0.5 rounded text-xs">074198609628.jpg</code></span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-[#2A5C43] mt-2 shrink-0" />
            <span><strong>Slug del producto:</strong> <code className="bg-white px-2 py-0.5 rounded text-xs">taste-of-the-wild-high-prairie-bisonte-venado.jpg</code></span>
          </li>
        </ul>
        <p className="text-xs text-[#57534E] mt-3">
          Todas las variantes del mismo producto (5 lb, 14 lb, 28 lb, etc.) compartirán la misma imagen frontal. Formatos aceptados: JPG, PNG, WEBP. Máximo 10 MB por imagen y 200 MB por ZIP.
        </p>
      </div>

      {/* Upload area */}
      <div className="bg-white rounded-3xl border border-[#E7E5E4] p-8 mb-6">
        {!selectedFile && !uploading && (
          <div
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-[#2A5C43]', 'bg-[#F7F2E8]/30'); }}
            onDragLeave={(e) => { e.currentTarget.classList.remove('border-[#2A5C43]', 'bg-[#F7F2E8]/30'); }}
            onDrop={(e) => {
              e.preventDefault();
              e.currentTarget.classList.remove('border-[#2A5C43]', 'bg-[#F7F2E8]/30');
              const file = e.dataTransfer.files?.[0];
              if (file) {
                const evt = { target: { files: [file] } };
                handleFileSelect(evt);
              }
            }}
            className="border-2 border-dashed border-[#E7E5E4] rounded-2xl p-12 text-center cursor-pointer hover:border-[#2A5C43] hover:bg-[#F7F2E8]/30 transition-colors"
            data-testid="admin-upload-dropzone"
          >
            <div className="w-16 h-16 rounded-full bg-[#F7F2E8] flex items-center justify-center mx-auto mb-4">
              <Upload className="w-8 h-8 text-[#2A5C43]" />
            </div>
            <p className="font-display font-semibold text-lg text-[#1C1917] mb-1">
              Arrastra tu ZIP aquí o haz clic para seleccionar
            </p>
            <p className="text-sm text-[#57534E]">Solo archivos .zip • Hasta 200 MB</p>
          </div>
        )}

        {selectedFile && !uploading && (
          <div className="flex items-center justify-between p-4 bg-[#F7F2E8]/50 rounded-2xl">
            <div className="flex items-center gap-3">
              <FileArchive className="w-8 h-8 text-[#2A5C43]" />
              <div>
                <p className="font-semibold text-[#1C1917]" data-testid="admin-selected-file">{selectedFile.name}</p>
                <p className="text-xs text-[#57534E]">{(selectedFile.size / (1024 * 1024)).toFixed(2)} MB</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={clearSelection}
                variant="outline"
                className="rounded-full"
                data-testid="admin-clear-file"
              >
                <X className="w-4 h-4" />
              </Button>
              <Button
                onClick={handleUpload}
                className="bg-[#2A5C43] hover:bg-[#214A35] text-white rounded-full px-6"
                data-testid="admin-upload-submit"
              >
                <Upload className="w-4 h-4 mr-2" /> Subir imágenes
              </Button>
            </div>
          </div>
        )}

        {uploading && (
          <div className="text-center py-8" data-testid="admin-upload-progress">
            <Loader2 className="w-10 h-10 animate-spin text-[#2A5C43] mx-auto mb-4" />
            <p className="font-display font-semibold text-[#1C1917] mb-2">Subiendo imágenes...</p>
            <p className="text-sm text-[#57534E] mb-4">
              {progress < 100 ? `Cargando archivo... ${progress}%` : 'Procesando y asociando a productos...'}
            </p>
            <div className="w-full max-w-md mx-auto h-2 bg-[#E7E5E4] rounded-full overflow-hidden">
              <div
                className="h-full bg-[#2A5C43] transition-all duration-300"
                style={{ width: `${progress || 5}%` }}
              />
            </div>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept=".zip,application/zip"
          onChange={handleFileSelect}
          className="hidden"
          data-testid="admin-file-input"
        />
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-6 flex items-start gap-3" data-testid="admin-upload-error">
          <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-red-800">Error</p>
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Result */}
      {result && (
        <div className="bg-white rounded-3xl border border-[#E7E5E4] overflow-hidden" data-testid="admin-upload-result">
          <div className="bg-[#2A5C43] text-white p-6">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-8 h-8" />
              <div>
                <p className="font-display font-bold text-xl">¡Carga completada!</p>
                <p className="text-sm text-white/80">{result.message}</p>
              </div>
            </div>
          </div>
          <div className="p-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-[#F7F2E8]/50 rounded-2xl">
              <p className="font-display font-bold text-3xl text-[#2A5C43]" data-testid="admin-processed-count">{result.processed}</p>
              <p className="text-xs uppercase tracking-widest text-[#8BA58F] font-semibold">Imágenes subidas</p>
            </div>
            <div className="text-center p-4 bg-[#F7F2E8]/50 rounded-2xl">
              <p className="font-display font-bold text-3xl text-[#D47A4A]" data-testid="admin-updated-count">{result.products_updated}</p>
              <p className="text-xs uppercase tracking-widest text-[#8BA58F] font-semibold">Productos actualizados</p>
            </div>
            <div className="text-center p-4 bg-[#F7F2E8]/50 rounded-2xl">
              <p className="font-display font-bold text-3xl text-[#57534E]">{result.skipped?.length || 0}</p>
              <p className="text-xs uppercase tracking-widest text-[#8BA58F] font-semibold">Omitidas</p>
            </div>
          </div>

          {(result.skipped?.length > 0 || result.errors?.length > 0) && (
            <details className="border-t border-[#E7E5E4]">
              <summary className="cursor-pointer p-4 text-sm font-medium text-[#57534E] hover:text-[#1C1917]">
                Ver detalles de omitidas y errores ({(result.skipped?.length || 0) + (result.errors?.length || 0)})
              </summary>
              <div className="p-4 pt-0 max-h-80 overflow-y-auto">
                {result.skipped?.map((s, i) => (
                  <div key={`skip-${i}`} className="text-xs py-1 border-b border-[#F7F2E8] flex gap-2">
                    <span className="text-[#8BA58F] font-medium min-w-[80px]">Omitida:</span>
                    <span className="text-[#57534E] flex-1"><code>{s.file}</code> — {s.reason}</span>
                  </div>
                ))}
                {result.errors?.map((e, i) => (
                  <div key={`err-${i}`} className="text-xs py-1 border-b border-[#F7F2E8] flex gap-2">
                    <span className="text-red-600 font-medium min-w-[80px]">Error:</span>
                    <span className="text-[#57534E] flex-1"><code>{e.file}</code> — {e.error}</span>
                  </div>
                ))}
              </div>
            </details>
          )}

          <div className="border-t border-[#E7E5E4] p-4 flex justify-between items-center">
            <p className="text-xs text-[#57534E]">Las imágenes ya son visibles en el catálogo</p>
            <Button asChild variant="outline" className="rounded-full border-[#2A5C43] text-[#2A5C43]">
              <Link to="/perros" data-testid="admin-view-catalog"><Package className="w-4 h-4 mr-2" /> Ver catálogo</Link>
            </Button>
          </div>
        </div>
      )}
    </div>
    </AdminLayout>
  );
};

export default AdminImages;
